/**
 * Client-side image compression using native Canvas API.
 * No external dependencies — works on all modern browsers.
 *
 * Reduces upload size by 10-50x (e.g. 5MB smartphone photo → 150KB WebP)
 * before sending to the server. The server's sharp pass is kept as a
 * safety net and for format normalization.
 */

const MAX_DIMENSION = 1600;
const WEBP_QUALITY = 0.78;

/**
 * Compress an image File to a WebP Blob, resizing to max 1600px on the
 * longest side. Returns the original file if compression fails or if the
 * result is larger than the original (already optimized).
 */
export async function compressImage(
  file: File,
  maxDimension = MAX_DIMENSION,
  quality = WEBP_QUALITY,
): Promise<File> {
  // Only compress raster images
  if (!file.type.startsWith('image/')) return file;
  // SVG: skip (vector, no benefit)
  if (file.type === 'image/svg+xml') return file;

  try {
    // Load image via object URL (works everywhere, no createImageBitmap needed)
    const url = URL.createObjectURL(file);
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = () => reject(new Error('load failed'));
      el.src = url;
    });
    URL.revokeObjectURL(url);

    // Calculate target dimensions (preserve aspect ratio)
    let { naturalWidth: width, naturalHeight: height } = img;
    if (width > maxDimension || height > maxDimension) {
      if (width >= height) {
        height = Math.round((height / width) * maxDimension);
        width = maxDimension;
      } else {
        width = Math.round((width / height) * maxDimension);
        height = maxDimension;
      }
    }

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return file;

    ctx.drawImage(img, 0, 0, width, height);
    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, 'image/webp', quality),
    );

    if (!blob || blob.size >= file.size) return file;

    return new File([blob], file.name.replace(/\.(jpe?g|png|webp|gif|bmp)$/i, '.webp'), {
      type: 'image/webp',
    });
  } catch {
    // If anything fails, return original file (server will handle it)
    return file;
  }
}
